import axios from '../../../../../lib/axios'
import { ChangeEvent, FC, useEffect, useState } from 'react'
import { Box, Checkbox, CircularProgress, IconButton, Typography } from '@mui/material'
import theme from 'theme'
import toast from 'react-hot-toast'

/* Utils */
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { closeModal, modalPropsSelector } from '../../../../../store/modals/modalSlice'
import { classifyDeleteOutcome, DeleteOutcome } from 'lib/deleteOutcome'
import { isStaleAccountSessionError } from 'lib/accountSessionState'

/* Components */
import { ThemeButton } from '../../../buttons'
import { DeleteIcon, InstanceCheckedIcon, InstanceUncheckedIcon, IntegrationInfoIcon } from '../../../svg'
import DisabledCheckboxSvg from '../../../svg/DisabledCheckboxSvg'
import {
  CPILoadingWrapper,
  DeleteInstanceActionsWrapper,
  DeleteInstanceCPICard,
  DeleteInstanceCPICardWrapper,
  DeleteInstanceSubtitle,
  DeleteInstanceTitle,
  DeleteInstanceWrapper,
  InfoTooltipWrapper,
  LoadingDeleteInstanceWrapper
} from './deleteIntegrationInstance.styles'
import { IntegrationHoverTooltip } from '../../../../pages/library/components/LibraryDetailsTooltip'
import IntegrationWarningIcon from '../../../svg/IntegrationWarningIcon'
import { HotToast } from '../../../toast'

interface activeCPI {
  name: string
  title: string
  displayName: string
  checked: boolean
  isOnly?: boolean
}

const DeleteIntegrationInstance: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const [loading, setLoading] = useState(false)
  const [cpisLoading, setCpisLoading] = useState(true)
  const [step, setStep] = useState(1)
  const [affectedCPIs, setAffectedCPIs] = useState<activeCPI[]>([])
  const [preservedCPIs, setNoPreservedCPIs] = useState<activeCPI[]>([])
  const instanceId = modalProps.props?.integrationInstance?.id

  useEffect(() => {
    findAffectedCpis().catch((e) => e)
  }, [])

  const findAffectedCpis = async () => {
    try {
      const res = await axios.get(`/api/v3/integrations/config/${instanceId}/cpis`)

      if (res.status === 200) {
        const cpis = res.data.cpis
        const haveInstance: activeCPI[] = []
        cpis.forEach(
          (n: { integrations: any; name: string; title: string; display_name: string; configuration: any }) => {
            haveInstance.push({
              name: n.name,
              displayName: n.display_name,
              title: n.title,
              checked: false,
              isOnly: n.configuration.integrations?.length <= 1
            })
          }
        )

        setAffectedCPIs(haveInstance.sort((a, b) => (a.displayName < b.displayName ? -1 : 0)))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setCpisLoading(false)
    }
  }

  useEffect(() => {
    findAffectedCpis().catch((e) => e)
  }, [])

  const handleClose = () => {
    dispatch(closeModal())
  }

  const getDeletingCPis = () => {
    const newArr = [...affectedCPIs]
    const listOfDeletingCpis = [] as activeCPI[]
    newArr.forEach((item) => {
      listOfDeletingCpis.push(item)
    })
    setNoPreservedCPIs(listOfDeletingCpis)
  }

  const changeHandler = (e: ChangeEvent<HTMLInputElement>, integration: activeCPI) => {
    const newArr = [...affectedCPIs]
    const findIndex = newArr.findIndex((item) => item.name === integration.name)
    newArr[findIndex].checked = e.target.checked
    setAffectedCPIs(newArr.sort((a, b) => (a.displayName < b.displayName ? -1 : 0)))
    getDeletingCPis()
  }

  const updateStep = () => {
    setStep(2)
    getDeletingCPis()
  }

  const notifyOutcome = (outcome: DeleteOutcome) => {
    const instanceName = modalProps.props?.integrationInstance?.name
    const integrationName = modalProps.props?.integrationInstance?.integration_name

    if (outcome === 'deleted') {
      toast.success(() => (
        <HotToast
          text="Instance Deleted"
          description={`Instance "${instanceName}" has been successfully removed from your ${integrationName} integration.`}
        />
      ))
    } else if (outcome === 'already-deleted') {
      toast.success(() => <HotToast text="Instance Deleted" description="This instance has already been removed." />)
    } else if (outcome === 'sync-in-progress') {
      toast(() => (
        <HotToast
          text="Data collection in progress"
          description={`We can't delete this instance while ${integrationName} is still collecting data. Please try again once the current collection finishes.`}
        />
      ))
    } else {
      toast.error(() => (
        <HotToast
          text="Couldn't delete instance"
          description="Something went wrong while deleting this instance. Please try again, or contact support if the problem continues."
        />
      ))
    }
  }

  const handleDelete = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    const listOfDeletingCpis = [] as string[]
    affectedCPIs.forEach((item) => {
      if (item.checked) {
        const name = item.name.toLowerCase().replace('-', '')
        listOfDeletingCpis.push(name)
      }
    })

    const body = {} as any
    if (listOfDeletingCpis.length) {
      body.cpis = listOfDeletingCpis
    }
    setLoading(true)

    let outcome: DeleteOutcome
    try {
      let res
      if (modalProps.props?.integrationInstance?.is_v2) {
        res = await axios.post(`/api/v4/products/${modalProps.props?.integrationInstance.instance_name}/remove`, {
          preserve_metric_slugs: listOfDeletingCpis
        })
      } else {
        if (Object.keys(body).length) {
          res = await axios.delete(`/api/v3/integrations/config/${instanceId}`, { data: body } as any)
        } else res = await axios.delete(`/api/v3/integrations/config/${instanceId}`)
      }

      outcome = classifyDeleteOutcome(res.status, res.data)
    } catch (e) {
      // The account went away mid-request: stop quietly rather than toast at whoever is here now.
      if (isStaleAccountSessionError(e)) return
      const response = (e as any).response
      outcome = classifyDeleteOutcome(response?.status, response?.data)
    } finally {
      setLoading(false)
    }

    notifyOutcome(outcome)
    handleClose()

    // A refusal leaves the instance in place, so the drawer and list already show the truth.
    if (outcome !== 'deleted' && outcome !== 'already-deleted') return

    if (modalProps.props?.closeCallback) {
      modalProps.props.closeCallback()
    }

    if (modalProps.props?.callback) {
      try {
        await modalProps.props.callback()
      } catch (err) {
        console.error('[DeleteIntegrationInstance] list refresh failed', err)
      }
    }
  }

  const InfoTooltipText = (
    <InfoTooltipWrapper>
      <Box className="header">
        <IntegrationInfoIcon size={20} />
        <Typography className="title">Data can be preserved</Typography>
      </Box>

      <Typography className="description">
        Deleting the instance is going to stop the data collection. However, preserving the data will allow you to still
        view the previous collected data.
      </Typography>
    </InfoTooltipWrapper>
  )

  const WarningTooltipText = (name: string, title: string) => {
    return (
      <InfoTooltipWrapper>
        <Box className="header">
          <IntegrationWarningIcon />
          <Typography className="title">CPI Deactivation</Typography>
        </Box>

        <Typography className="description">
          “{modalProps.props?.integrationInstance?.integration_name}” is the only source that {name} ({title}) is
          connected with. Deleting this instance will deactivate {name} and all data will be permanently deleted.
        </Typography>
      </InfoTooltipWrapper>
    )
  }

  if (cpisLoading) {
    return (
      <LoadingDeleteInstanceWrapper>
        <CPILoadingWrapper>
          <CircularProgress size="40px" color="secondary" />
        </CPILoadingWrapper>
      </LoadingDeleteInstanceWrapper>
    )
  }

  const integrationInstance = modalProps?.props?.integrationInstance
  if (!integrationInstance) return null
  return (
    <DeleteInstanceWrapper>
      {step === 1 ? (
        <>
          {!affectedCPIs.length ? (
            <>
              <DeleteInstanceTitle>
                Delete Instance “
                {integrationInstance.is_v2 ? integrationInstance.instance_name : integrationInstance.name}”
              </DeleteInstanceTitle>
              <DeleteInstanceSubtitle>No CPIs will be affected if you delete this Instance.</DeleteInstanceSubtitle>
              <DeleteInstanceActionsWrapper>
                <ThemeButton themevariant="tertiary" onClick={handleClose} disabled={loading}>
                  Go Back
                </ThemeButton>
                <ThemeButton
                  themevariant="primary"
                  type="button"
                  onClick={(e) => handleDelete(e)}
                  disabled={loading}
                  endIcon={loading && <CircularProgress size="20px" />}
                >
                  <>
                    <DeleteIcon />
                    Delete
                  </>
                </ThemeButton>
              </DeleteInstanceActionsWrapper>
            </>
          ) : (
            <Box>
              <DeleteInstanceTitle>
                Delete Instance “
                {integrationInstance.is_v2 ? integrationInstance.instance_name : integrationInstance.name}”
              </DeleteInstanceTitle>

              <DeleteInstanceSubtitle>Deleting this Instance affects following CPIs :</DeleteInstanceSubtitle>

              <DeleteInstanceCPICardWrapper noBg={false}>
                {affectedCPIs.map((item) => {
                  return (
                    <DeleteInstanceCPICard key={item.name} justifyCenter={false} step={1}>
                      <Box className="cpi">{item.displayName}</Box>

                      <Typography className="name">{item.title}</Typography>

                      {item.isOnly ? (
                        <IntegrationHoverTooltip title={WarningTooltipText(item.displayName, item.title)}>
                          <IconButton>
                            <IntegrationWarningIcon />
                          </IconButton>
                        </IntegrationHoverTooltip>
                      ) : (
                        <IntegrationHoverTooltip title={InfoTooltipText}>
                          <IconButton>
                            <IntegrationInfoIcon size={20} />
                          </IconButton>
                        </IntegrationHoverTooltip>
                      )}

                      <Box display="flex" alignItems="center">
                        {item.isOnly ? (
                          <Box
                            sx={{
                              marginLeft: `${theme.spacing.xSmall}px`,
                              marginRight: '9px',
                              display: 'flex',
                              svg: { path: { stroke: 'rgb(208, 195, 204)' } }
                            }}
                          >
                            <InstanceUncheckedIcon />
                          </Box>
                        ) : (
                          <Checkbox
                            inputProps={{ 'aria-label': 'Checkbox' }}
                            checked={item.checked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => changeHandler(e, item)}
                            disabled={item.isOnly}
                            icon={<InstanceUncheckedIcon />}
                            checkedIcon={<InstanceCheckedIcon />}
                          />
                        )}
                        <Typography className={`${item.isOnly ? 'disabled-label' : ''} label`}>
                          Preserve Data
                        </Typography>
                      </Box>
                    </DeleteInstanceCPICard>
                  )
                })}

                {modalProps.props?.isAnalyzer ? (
                  <DeleteInstanceCPICard key="analyzer" justifyCenter={false} step={1}>
                    <Box className="cpi-empty"></Box>

                    <Typography className="name">Asset Coverage Analyzer</Typography>

                    <IntegrationHoverTooltip
                      title={
                        <InfoTooltipWrapper>
                          <Typography className="description small">
                            You will no longer be able to use{' '}
                            {integrationInstance.is_v2 ? integrationInstance.instance_name : integrationInstance.name}{' '}
                            as a data source for the Asset Coverage Analyzer.
                          </Typography>
                        </InfoTooltipWrapper>
                      }
                    >
                      <IconButton>
                        <IntegrationWarningIcon />
                      </IconButton>
                    </IntegrationHoverTooltip>

                    <Box display="flex" alignItems="center">
                      <Box sx={{ marginLeft: `${theme.spacing.xSmall}px`, marginRight: '9px', display: 'flex' }}>
                        <DisabledCheckboxSvg />
                      </Box>
                      <Typography className={`disabled-label label`}>Preserve Data</Typography>
                    </Box>
                  </DeleteInstanceCPICard>
                ) : (
                  ''
                )}
              </DeleteInstanceCPICardWrapper>

              <DeleteInstanceActionsWrapper>
                <ThemeButton themevariant="tertiary" onClick={handleClose}>
                  Go Back
                </ThemeButton>
                <ThemeButton themevariant="primary" type="button" onClick={() => updateStep()}>
                  <>
                    <DeleteIcon />
                    Delete
                  </>
                </ThemeButton>
              </DeleteInstanceActionsWrapper>
            </Box>
          )}
        </>
      ) : (
        <>
          <DeleteInstanceTitle>Are You Sure?</DeleteInstanceTitle>
          <DeleteInstanceSubtitle>
            Please review how the deletion is affecting the following CPIs and confirm :
          </DeleteInstanceSubtitle>

          <DeleteInstanceCPICardWrapper noBg={false}>
            {preservedCPIs.map((item) => {
              return (
                <DeleteInstanceCPICard key={item.name} justifyCenter={true} step={2}>
                  <Box className="cpi">{item.displayName}</Box>

                  <Typography className="name">
                    {item.checked
                      ? `Instance data will be preserved for ${item.displayName}`
                      : item.isOnly
                      ? `Instance data is going to be permanently deleted & ${item.displayName} deactivated`
                      : `Instance data is going to be permanently deleted for ${item.displayName}`}
                  </Typography>
                </DeleteInstanceCPICard>
              )
            })}
          </DeleteInstanceCPICardWrapper>

          <DeleteInstanceActionsWrapper>
            <ThemeButton themevariant="tertiary" onClick={() => setStep(1)} disabled={loading}>
              Go Back
            </ThemeButton>

            <ThemeButton
              themevariant="primary"
              type="button"
              onClick={handleDelete}
              disabled={loading}
              endIcon={loading && <CircularProgress size="20px" />}
            >
              <>
                <DeleteIcon />
                Confirm and Delete
              </>
            </ThemeButton>
          </DeleteInstanceActionsWrapper>
        </>
      )}
    </DeleteInstanceWrapper>
  )
}

export default DeleteIntegrationInstance
